import { useContainerBlockContext } from '@lighthouse/block'
import type { RecordPoint } from '@lighthouse/core'
import { SelectedMode } from '@lighthouse/core'
import { useAtomAction, useAtomData } from '@lighthouse/shared'
import { useCallback, useMemo } from 'react'

import { setViewRuntimeStateAtom } from '@/atoms/page/action'
import { viewRuntimeStateAtomFamily } from '@/atoms/page/state'
import { useCurrentStackIdContext } from '@/contexts/PageContext'

interface useSelectIdsPayload {
    blockId: string
    recordPoints: RecordPoint[]
}

export const useSelectIds = (payload: useSelectIdsPayload) => {
    const { blockId, recordPoints } = payload
    const { scope } = useContainerBlockContext()
    const stackId = useCurrentStackIdContext()
    const { run: setViewRuntimeState } = useAtomAction(setViewRuntimeStateAtom)

    const selectState = useAtomData(viewRuntimeStateAtomFamily(stackId))
    const currentSelectState = useMemo(() => {
        if (!selectState) {
            return
        }
        const isSelf = blockId === selectState.viewId && selectState.scope === scope
        if (!isSelf) {
            return
        }
        return selectState
    }, [selectState, blockId, scope])

    const { selectedIds, selectedMode } = useMemo(() => {
        if (!currentSelectState) {
            return { selectedIds: [] as string[], selectedMode: undefined }
        }
        return currentSelectState
    }, [currentSelectState])

    const handleSelectedRecords = useCallback(
        (ids: string[]) => {
            setViewRuntimeState({
                stackId,
                viewId: blockId,
                scope,
                selectedMode: SelectedMode.CURRENT_PAGE,
                selectedIds: ids
            })
        },
        [setViewRuntimeState, stackId, blockId, scope]
    )

    const handleSelectModeChange = useCallback(
        (val?: SelectedMode) => {
            const selectedIds = val ? recordPoints.map(point => point.id) : []
            setViewRuntimeState({
                stackId,
                viewId: blockId,
                scope,
                selectedMode: val,
                selectedIds
            })
        },
        [blockId, recordPoints, scope, setViewRuntimeState, stackId]
    )

    return { selectedIds, handleSelectedRecords, handleSelectModeChange }
}
